import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Tag, Calendar, TrendingUp, Filter, User, Film, Zap, CalendarDays, Facebook } from 'lucide-react';
import { imageUrl } from '../config/images';
import { useAuth } from '../contexts/AuthContext';
import { useLogger } from '../contexts/LoggerContext';
import LoginModal from './LoginModal';
import SiteHeader from './SiteHeader';
import DashboardManager from './DashboardManager';
import InsiderCarousel from './InsiderCarousel';
import IntelligenceFeed from './IntelligenceFeed';
import UpcomingByDate from './UpcomingByDate';
import IntelligentResults from './IntelligentResults';
import { searchResultHref } from '../utils/searchResultHref';
import { logInteraction } from '../utils/interactions';
// Temporarily disable tracing imports to fix page loading
// import { tracer, createUserActionSpan, createSearchSpan, addUserContext } from '../tracing';
// import { trace } from '@opentelemetry/api';

// Clickable tag chips with a working "+N more" toggle that expands to show all
// tags (and collapses again). Used across the search result cards.
function TagList({ tags, limit = 3, onTagClick, variant = 'gray' }) {
  const [expanded, setExpanded] = useState(false);
  if (!tags || tags.length === 0) return null;
  const shown = expanded ? tags : tags.slice(0, limit);
  const tagCls = variant === 'brand'
    ? 'bg-videbits-100 hover:bg-videbits-200 text-videbits-700 hover:text-videbits-900'
    : 'bg-gray-100 hover:bg-videbits-100 text-gray-600 hover:text-videbits-700';
  const moreCls = variant === 'brand'
    ? 'bg-videbits-100 hover:bg-videbits-200 text-videbits-700'
    : 'bg-gray-100 hover:bg-gray-200 text-gray-600';
  return (
    <div className="flex flex-wrap gap-1 mt-3">
      {shown.map((tag, index) => (
        <button
          key={index}
          type="button"
          onClick={(e) => { e.stopPropagation(); onTagClick(tag, e); }}
          className={`px-2 py-1 ${tagCls} text-xs rounded-full cursor-pointer transition-colors duration-200 border-none`}
          title={`Search for more "${tag}" content`}
        >
          {tag}
        </button>
      ))}
      {tags.length > limit && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded((v) => !v); }}
          className={`px-2 py-1 ${moreCls} text-xs rounded-full cursor-pointer transition-colors`}
        >
          {expanded ? 'Show less' : `+${tags.length - limit} more`}
        </button>
      )}
    </div>
  );
}

// How long to wait for the type-ahead before giving up on it. /search/suggest
// is a DB-only lookup that normally answers in ~10ms, so anything near this is
// already broken from the user's point of view; a submit then falls back to the
// full catalogue search instead of appearing to do nothing.
const SUGGEST_TIMEOUT_MS = 2000;

const Home = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  // True once a real search has run — drives the landing↔results view switch so
  // the hero/carousels stay put while typing (they only swap out when a search
  // actually fires, not on the first keystroke).
  const [hasSearched, setHasSearched] = useState(false);
  // Keyboard-highlighted suggestion in the autocomplete dropdown (-1 = none).
  const [activeIndex, setActiveIndex] = useState(-1);
  const activeItemRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);
  // The submitted query for the AI intelligence layer — set on submit so it
  // runs alongside the catalogue search, not on every keystroke.
  const [aiQuery, setAiQuery] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState('login');
  const [parsedSearch, setParsedSearch] = useState({
    query: '',
    crew: '',
    tags: '',
    category: ''
  });
  const [llmMetadata, setLlmMetadata] = useState({
    enhanced: false,
    provider: null,
    confidence: null,
    reasoning: null
  });
  const [didYouMean, setDidYouMean] = useState(null);
  // True when the catalogue provider (TMDB) was unavailable and we fell back to
  // the thin local DB — so we can tell the user instead of showing empty results.
  const [searchDegraded, setSearchDegraded] = useState(false);
  // Live-sport "where to watch" match (F1, Premier League…) — not in TMDB.
  const [sportsResult, setSportsResult] = useState(null);
  // Landing carousels are now rendered by <DashboardCarousels>, which owns
  // its own fetching (see DEFAULT_SECTIONS in config/sectionTypes.js).

  // Get authentication state and logger
  const { isAuthenticated, loading: authLoading, token } = useAuth();
  const log = useLogger();
  const location = useLocation();
  const navigate = useNavigate();

  // Handle opening login modal
  const openLoginModal = (mode = 'login') => {
    setLoginModalMode(mode);
    setShowLoginModal(true);
  };

  // API base URL - defaults to local development
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Homepage release-digest signup (reuses the public double-opt-in endpoint).
  const [digestEmail, setDigestEmail] = useState('');
  const [digestStatus, setDigestStatus] = useState(null); // 'sending' | 'ok' | 'error'
  const [digestError, setDigestError] = useState('');

  const submitDigest = async (e) => {
    e.preventDefault();
    if (!digestEmail.trim()) return;
    setDigestStatus('sending');
    try {
      const resp = await fetch(`${API_BASE}/seo/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          // Ask for JSON so we can tell a rejected address (400) apart from a
          // rate limit (429); without this the endpoint serves the SEO HTML
          // form and every failure looked like "invalid email".
          Accept: 'application/json',
        },
        body: new URLSearchParams({ email: digestEmail.trim(), source: 'home_band', website: '' }),
      });
      if (resp.ok) {
        setDigestStatus('ok');
        setDigestEmail('');
        return;
      }
      if (resp.status === 429) {
        setDigestError('Too many attempts just now — please try again in a little while.');
      } else if (resp.status === 400) {
        const data = await resp.json().catch(() => null);
        setDigestError(data?.error || 'Please enter a valid email and try again.');
      } else {
        setDigestError("Something went wrong on our end — please try again.");
      }
      setDigestStatus('error');
    } catch (err) {
      setDigestError("Couldn't reach us just now — please check your connection and try again.");
      setDigestStatus('error');
    }
  };


  // Load search history on component mount and cleanup timers
  useEffect(() => {
    loadSearchHistory();
    
    // Cleanup function to clear any pending timers
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  // Handle URL search parameters (for tag clicks from MovieDetail)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParam = searchParams.get('q');
    const tagSearchParam = searchParams.get('tag_search');
    const loginParam = searchParams.get('login');
    
    if (queryParam && queryParam !== searchQuery) {
      setSearchQuery(queryParam);
      // Check if this is a tag-based search from URL parameters
      const isTagSearch = tagSearchParam === 'true';
      debouncedSearch(queryParam, isTagSearch, true);
    }

    // Check for login parameter (from email verification redirect)
    if (loginParam === 'true' && !isAuthenticated && !showLoginModal) {
      openLoginModal('login');
    }
  }, [location.search, isAuthenticated, showLoginModal]);


  const loadSearchHistory = () => {
    try {
      const history = localStorage.getItem('videbit-search-history');
      if (history) {
        setSearchHistory(JSON.parse(history));
      }
    } catch (error) {
      console.error('Error loading search history:', error);
    }
  };

  const saveSearchToHistory = (query) => {
    if (!query.trim()) return;
    
    try {
      const updatedHistory = [query, ...searchHistory.filter(item => item !== query)].slice(0, 5);
      setSearchHistory(updatedHistory);
      localStorage.setItem('videbit-search-history', JSON.stringify(updatedHistory));
    } catch (error) {
      console.error('Error saving search history:', error);
    }
  };


  // Debounced search function
  const debouncedSearch = useCallback(async (query, isTagSearch = false, isSubmit = false) => {
    if (!query.trim()) {
      setSearchResults([]);
      setError('');
      setSearchDegraded(false);
      setSportsResult(null);
      setHasSearched(false);
      setParsedSearch({ query: '', crew: '', tags: '', category: '' });
      return;
    }
    // Only an explicit submit (Enter / tag / history) switches to the full
    // results view. Plain typing just fetches rows to show as suggestions, so
    // the landing page stays put (Google-style).
    if (isSubmit) setHasSearched(true);
    setSearchDegraded(false);
    setSportsResult(null);

    // Parse the query for special keywords
    const parsed = parseSearchQuery(query);
    setParsedSearch(parsed);

    // Clear old results immediately to prevent showing stale data
    setSearchResults([]);
    setDidYouMean(null);
    setLoading(true);
    setError('');

    // Log search start
    const searchStartTime = Date.now();
    log.info('Search started', {
      query: query.trim(),
      isTagSearch,
      hasCategory: !!parsed.category,
      hasCrew: !!parsed.crew,
      hasTags: !!parsed.tags
    });

    try {
      const params = new URLSearchParams({
        q: query.trim(),  // Send full original query to backend LLM
        limit: '20',
        grouped: 'true',  // Enable grouped results
        // Type-ahead suggestions skip the (slow) LLM query enhancement for a
        // snappy dropdown; an explicit submit (Enter/tag/history) keeps it.
        use_llm: isSubmit ? 'true' : 'false',
        _t: Date.now()   // Cache buster to ensure fresh results every time
      });

      // Add tag_search parameter when this is a tag click
      if (isTagSearch) {
        params.append('tag_search', 'true');
      }

      const response = await fetch(`${API_BASE}/search?${params}`, {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store',  // Prevent browser from caching search results
      });

      if (response.ok) {
        const data = await response.json();
        const searchDuration = Date.now() - searchStartTime;

        // Capture LLM metadata
        const llmData = {
          enhanced: data.llm_enhanced || false,
          provider: data.llm_provider || null,
          confidence: data.llm_confidence || null,
          reasoning: data.llm_reasoning || null
        };
        setLlmMetadata(llmData);
        setSearchDegraded(!!data.tmdb_unavailable);
        setSportsResult(data.sports || null);

        // Debug: Log LLM metadata
        console.log('🤖 LLM Metadata:', llmData);
        if (llmData.enhanced) {
          console.log('✅ LLM Badge should be visible!');
        } else {
          console.log('❌ LLM not enhanced - no badge');
        }

        // Handle grouped results
        if (data.grouped && data.groups) {
          // Merge every group into a single list ranked by popularity, so the
          // most notable results lead regardless of category (film/TV/person).
          const allResults = [
            ...(data.groups.Movies?.results || []),
            ...(data.groups["TV Shows"]?.results || []),
            // Games come from our own catalogue, never from TMDb, and carry
            // IGDB's rating in place of a TMDb popularity — see the Games group
            // in search_tmdb.py.
            ...(data.groups.Games?.results || []),
            ...(data.groups.People?.results || [])
          ].sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
          // VideBits Insider projects (in-development, incl. approved submissions
          // not in TMDb) — surfaced first so a specific title match is easy to spot.
          const insiderResults = data.groups["In Development"]?.results || [];
          setSearchResults([...insiderResults, ...allResults]);
          setDidYouMean(data.did_you_mean || null);

          // Results render as one popularity-ranked list, not per-category
          // sections. The per-category branches this used to switch on were
          // removed once nothing could reach them.

          // The funnel's denominator. Only on an explicit submit: the
          // type-ahead path re-runs this on every debounced keystroke, and
          // logging those would count one search per character typed.
          if (isSubmit) {
            logInteraction('search', {
              query: query.trim(),
              resultsCount: insiderResults.length + allResults.length,
              source: data.source,
            });
          }

          // Log successful search with details
          log.info('Search completed', {
            query: query.trim(),
            resultsCount: allResults.length,
            moviesCount: data.groups.Movies?.results?.length || 0,
            tvShowsCount: data.groups["TV Shows"]?.results?.length || 0,
            gamesCount: data.groups.Games?.results?.length || 0,
            peopleCount: data.groups.People?.results?.length || 0,
            durationMs: searchDuration,
            isTagSearch,
            llmEnhanced: data.llm_enhanced,
            llmProvider: data.llm_provider
          });
        } else {
          setSearchResults(Array.isArray(data.results) ? data.results : []);
          setDidYouMean(data.did_you_mean || null);

          if (isSubmit) {
            logInteraction('search', {
              query: query.trim(),
              resultsCount: Array.isArray(data.results) ? data.results.length : 0,
              source: data.source,
            });
          }

          // Log successful search
          log.info('Search completed', {
            query: query.trim(),
            resultsCount: data.results?.length || 0,
            durationMs: searchDuration,
            isTagSearch,
            llmEnhanced: data.llm_enhanced,
            llmProvider: data.llm_provider
          });
        }
      } else if (response.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
        setSearchResults([]);
        log.warn('Search rate limited', {
          query: query.trim(),
          statusCode: 429
        });
      } else if (response.status === 400) {
        setError('Invalid search query. Please check your input.');
        setSearchResults([]);
        log.warn('Invalid search query', {
          query: query.trim(),
          statusCode: 400
        });
      } else {
        setError('Search failed. Please try again.');
        setSearchResults([]);
        log.error('Search failed', {
          query: query.trim(),
          statusCode: response.status
        });
      }
    } catch (error) {
      setError('Unable to connect to search service.');
      setSearchResults([]);
      const searchDuration = Date.now() - searchStartTime;
      log.error('Search network error', {
        query: query.trim(),
        error: error.message,
        durationMs: searchDuration
      });
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  // Submitting from the search box (button / Enter) is pure autocomplete: it
  // DB-first type-ahead: suggestions come from OUR catalogue via /search/suggest
  // (indexed title + person lookup, ~10ms) instead of a live TMDB round-trip per
  // keystroke. Returns items in the same shape the dropdown + openSuggestion use.
  // Returns the suggestions on success, or null if the type-ahead could not
  // answer (network error, non-OK response, or slower than SUGGEST_TIMEOUT_MS).
  // Callers use that null to fall back to the full catalogue search rather than
  // leaving the user with a search box that appears to do nothing.
  const fetchSuggestions = useCallback(async (query) => {
    const qq = (query || '').trim();
    if (qq.length < 2) { setSearchResults([]); return []; }
    // NB: do NOT setHasSearched(true) here — that collapses the hero into the old
    // split results view. Type-ahead keeps hero-mode; the dropdown shows via
    // showSearchHistory + searchResults. Only an explicit submit leaves hero-mode.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SUGGEST_TIMEOUT_MS);
    try {
      const resp = await fetch(`${API_BASE}/search/suggest?q=${encodeURIComponent(qq)}&limit=8`, {
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      const results = data.results || [];
      setSearchResults(results);
      return results;
    } catch (e) {
      // Aborted (too slow) or the network failed. Keep any existing suggestions
      // on screen — they are still better than an empty dropdown — but tell the
      // caller we have nothing fresh, so a submit can fall back to /search.
      return null;
    } finally {
      clearTimeout(timer);
    }
  }, [API_BASE]);

  // Submitting searches the query string and shows the results view. It does
  // NOT open the top suggestion: guessing which result the user meant sends
  // them to a title page they never chose, and there is no way back to the
  // other matches except retyping. Picking a suggestion stays an explicit act —
  // click one, or arrow to it and press Enter.
  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (!q) return;
    saveSearchToHistory(q);
    setShowSearchHistory(false);
    setActiveIndex(-1);
    await debouncedSearch(q, false, true);
  };

  // Handle search input changes with debouncing
  const handleSearchInputChange = useCallback((value) => {
    setSearchQuery(value);
    
    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    // Auto-fetch suggestions once the query is meaningful (2+ chars). DB-first
    // suggestions are cheap (~10ms) so we can suggest earlier and with a shorter
    // debounce than the old live-TMDB path.
    if (value.trim().length >= 2) {
      const timer = setTimeout(() => {
        fetchSuggestions(value);
      }, 180); // snappy autocomplete
      setDebounceTimer(timer);
    } else {
      // Under 2 chars: no search — clear any stale suggestions.
      setSearchResults([]);
      setError('');
      setLoading(false);
      if (value.trim().length === 0) setHasSearched(false);
    }
  }, [debounceTimer, fetchSuggestions]);

  // Open a single suggestion (its detail page). Shared by click + keyboard.
  // `rank` is the 1-based row in the dropdown, so the funnel can tell a click
  // on the top suggestion from one the user had to scan down for.
  const openSuggestion = (r, rank) => {
    if (!r) return;
    // Insider projects link to their (gated) Insider page; everything else to
    // the TMDb-keyed movie/tv/person detail route.
    const to = searchResultHref(r);
    setShowSearchHistory(false);
    setActiveIndex(-1);
    sessionStorage.setItem(`movie_${r.id}`, JSON.stringify(r));
    logInteraction('suggestion_click', {
      tmdbId: r.id,
      mediaType: r.category === 'Person' ? 'person'
        : r.category === 'TV Series' ? 'tv' : 'movie',
      title: r.title,
      query: searchQuery,
      rank,
      resultsCount: searchResults.length,
    });
    navigate(to);
  };

  // Keyboard nav for the autocomplete dropdown: ↑/↓ move the highlight, Enter
  // opens the highlighted result (or runs the full search if none), Esc closes.
  const handleInputKeyDown = (e) => {
    const items = searchResults.slice(0, 20);
    const open = showSearchHistory && items.length > 0;
    if (e.key === 'ArrowDown') {
      if (!open) return;
      e.preventDefault();
      setShowSearchHistory(true);
      setActiveIndex((i) => (i + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      if (!open) return;
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      // Enter opens a suggestion only when the user has actually highlighted
      // one with the arrow keys. With nothing highlighted it searches the query
      // string, rather than assuming the top suggestion was what they meant.
      if (open && activeIndex >= 0) {
        e.preventDefault();
        openSuggestion(items[activeIndex], activeIndex + 1);
      } else {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      setShowSearchHistory(false);
      setActiveIndex(-1);
    }
  };

  // Reset the keyboard highlight whenever the suggestion set changes.
  useEffect(() => { setActiveIndex(-1); }, [searchResults]);
  // Keep the highlighted suggestion scrolled into view within the dropdown.
  useEffect(() => { activeItemRef.current?.scrollIntoView({ block: 'nearest' }); }, [activeIndex]);

  const handleHistoryClick = async (historyItem) => {
    setSearchQuery(historyItem);
    if (!historyItem.trim()) return;
    saveSearchToHistory(historyItem);
    setShowSearchHistory(true);
    // Re-run as suggestions (no isSubmit → no screen switch), same as typing.
    debouncedSearch(historyItem);
  };

  const handleTagClick = async (tag) => {
    // For genre tags, create natural language queries that work with smart parsing
    const genreWords = ['action', 'horror', 'comedy', 'drama', 'thriller', 'sci-fi', 'science-fiction', 'animation', 'adventure', 'crime', 'fantasy'];
    
    let tagQuery;
    if (genreWords.includes(tag.toLowerCase())) {
      // For genre tags, use natural language that triggers genre search
      tagQuery = `${tag} movies`;
    } else {
      // For other tags (actors, directors, years, etc.), search directly
      tagQuery = tag;
    }
    
    setSearchQuery(tagQuery);
    
    if (!tag.trim()) return;
    
    saveSearchToHistory(tagQuery);
    await debouncedSearch(tagQuery, true, true); // tag search, explicit submit
  };

  // Parse search query for special keywords
  const parseSearchQuery = (query) => {
    // SIMPLIFIED: Let the backend LLM handle ALL query parsing
    // The backend has sophisticated LLM-based query enhancement that understands
    // natural language queries like "movies with tom hanks" correctly
    // Frontend manual parsing was interfering with backend LLM
    const parsed = {
      query: query.trim(),
      crew: '',
      tags: '',
      category: ''
    };

    return parsed;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  // Safe highlighting component that doesn't use dangerouslySetInnerHTML
  const SafeHighlight = ({ text, query, className = "" }) => {
    if (!query) return <span className={className}>{text}</span>;
    
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = text.split(regex);
    
    return (
      <span className={className}>
        {parts.map((part, index) =>
          regex.test(part) ?
            <mark key={index} className="bg-videbits-200">{part}</mark> :
            part
        )}
      </span>
    );
  };

  // Landing chrome (tall gradient hero + carousels) shows until a search runs;
  // the search bar itself is rendered ONCE, outside this flag, so the same
  // <input> stays mounted across the landing↔results switch and never loses focus.
  const heroMode = !hasSearched;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SiteHeader />

      {/* Search band — ALWAYS rendered. In heroMode it's the tall gradient hero;
          once searching it collapses to a compact top bar. The single search
          input lives here in both states, so focus persists across the switch. */}
      <div
        className={`relative px-4 transition-all duration-500 ease-in-out ${heroMode ? 'py-20 md:py-36' : 'py-5 border-b border-gray-100'}`}
      >
        {/* Gradient hero backdrop — fades out on search instead of snapping off. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none transition-opacity duration-500 ease-in-out"
          style={{ background: 'linear-gradient(160deg, #fae8fa 0%, #ebb0ea 50%, #f5d0f4 100%)', opacity: heroMode ? 1 : 0 }}
        />
        <div className={`relative mx-auto w-full transition-all duration-500 ease-in-out ${heroMode ? 'max-w-3xl text-center' : 'max-w-4xl'}`}>
          {/* Hero copy — collapses smoothly (kept mounted) rather than popping out. */}
          <div className={`overflow-hidden transition-all duration-500 ease-in-out ${heroMode ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="flex justify-center mb-6">
              <img src="/logo-transparent.svg" alt="VideBits" className="w-14 h-14 opacity-90" />
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-videbits-900 mb-6 leading-tight tracking-tight">
              Be in the know
            </h1>
            <p className="max-w-2xl mx-auto mb-8 text-sm sm:text-base text-videbits-700">
              Film, TV and games. One connected entertainment intelligence platform for audiences and industry professionals.
            </p>
            {!isAuthenticated && (
              <p className="mb-10 text-sm sm:text-base text-videbits-600">
                <button type="button" onClick={() => openLoginModal('signup')} className="text-btn font-semibold text-videbits-800 underline underline-offset-2 hover:text-videbits-900">Create your account</button>{' '}
                to build your watchlist, or{' '}
                <button type="button" onClick={() => document.getElementById('mailing-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-btn font-semibold text-videbits-800 underline underline-offset-2 hover:text-videbits-900">Join the mailing list</button>.
              </p>
            )}
          </div>

          {/* THE single search bar — never unmounts */}
          <div className={heroMode ? 'relative w-full max-w-2xl mx-auto' : 'relative w-full'}>
            <div className={heroMode ? 'flex bg-white rounded-2xl shadow-2xl overflow-hidden' : 'flex gap-3'}>
              <div className="flex-1 relative min-w-0">
                <input
                  type="text"
                  placeholder="Search films, TV, games, people..."
                  value={searchQuery}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  onFocus={() => setShowSearchHistory(true)}
                  onBlur={(e) => {
                    if (!e.relatedTarget || !e.relatedTarget.closest('.search-history-dropdown')) {
                      setTimeout(() => setShowSearchHistory(false), 150);
                    }
                  }}
                  className={heroMode
                    ? 'w-full min-w-0 px-6 py-5 text-gray-900 text-lg focus:outline-none bg-transparent placeholder-gray-400'
                    : 'w-full px-4 py-4 border border-gray-300 rounded-full text-base focus:outline-none focus:ring-2 focus:ring-videbits-500 focus:border-videbits-500 shadow-sm hover:shadow-md transition-all'}
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={loading}
                className={heroMode
                  ? 'px-7 bg-videbits-500 hover:bg-videbits-600 text-white font-semibold text-base transition-colors disabled:opacity-50 flex items-center gap-2 flex-shrink-0'
                  : 'px-6 md:px-8 py-4 bg-videbits-600 text-white rounded-full hover:bg-videbits-700 transition-colors disabled:bg-gray-100 disabled:text-gray-500 border border-videbits-600 shadow-sm hover:shadow-md font-medium flex-shrink-0'}
              >
                {heroMode ? (
                  <><Search className="w-5 h-5" /><span className="hidden sm:inline">Search</span></>
                ) : (loading ? 'Thinking...' : 'Search')}
              </button>
            </div>

            {/* Autocomplete dropdown — a sibling of the input row (NOT inside the
                overflow-hidden hero box, which would clip it). Live suggestions
                while typing; recent searches when the box is empty. */}
            {showSearchHistory && (searchResults.length > 0 || (!searchQuery.trim() && searchHistory.length > 0)) && (
              <div className="search-history-dropdown absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto overscroll-contain text-left">
                <div className="p-2">
                  {searchResults.length > 0 ? (
                    <>
                      {searchResults.slice(0, 20).map((r, idx) => {
                        const poster = r.poster_path
                          ? imageUrl(r.poster_path, 'w92')
                          : null;
                        const isActive = idx === activeIndex;
                        return (
                          <button
                            // A game's id is 0 — it has no TMDb id — so two
                            // games would collide on `category-id`.
                            key={`${r.category}-${r.project_id || r.id}`}
                            ref={isActive ? activeItemRef : null}
                            onMouseEnter={() => setActiveIndex(idx)}
                            onMouseDown={(e) => { e.preventDefault(); openSuggestion(r, idx + 1); }}
                            className={`w-full text-left flex items-center gap-3 px-2 py-2 rounded-lg ${isActive ? 'bg-videbits-50' : 'hover:bg-videbits-50'}`}
                          >
                            {poster ? (
                              <img src={poster} alt="" className="w-8 h-11 object-cover rounded flex-shrink-0 bg-gray-100" />
                            ) : (
                              <div className="w-8 h-11 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <Film className="w-4 h-4 text-gray-400" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">{r.title}</div>
                              <div className="text-xs text-gray-500">
                                {r.category}{r.vote_average ? ` · ★ ${r.vote_average.toFixed(1)}` : ''}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      <div className="text-xs text-gray-500 px-3 py-1 border-b border-gray-100 mb-1">Recent searches</div>
                      {searchHistory.map((historyItem, index) => (
                        <button
                          key={index}
                          onMouseDown={(e) => { e.preventDefault(); handleHistoryClick(historyItem); }}
                          className="w-full text-left px-3 py-2 text-sm text-videbits-700 hover:bg-videbits-50 rounded-lg flex items-center space-x-2"
                        >
                          <Search className="w-3 h-3 text-videbits-500 flex-shrink-0" />
                          <span>{historyItem}</span>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Genre chips — collapse smoothly (kept mounted). Taller cap on
              mobile: the 9 chips wrap to 4-5 rows on a narrow viewport, so a
              desktop-sized max-h-32 clips the lower rows under overflow-hidden. */}
          <div className={`overflow-hidden transition-all duration-500 ease-in-out ${heroMode ? 'max-h-64 sm:max-h-32 opacity-100 mt-8' : 'max-h-0 opacity-0 mt-0'}`}>
            <div className="flex flex-wrap justify-center gap-2">
              {['Action', 'Drama', 'Comedy', 'Horror', 'Sci-Fi', 'Thriller', 'Romance', 'Animation', 'Documentary'].map((label) => (
                <button
                  key={label}
                  onClick={() => handleTagClick(label)}
                  className="px-4 py-2 rounded-full text-sm font-medium text-videbits-800 border border-videbits-400/50 bg-videbits-500/15 hover:bg-videbits-500/25 transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Landing content (carousels + features) — shown when no search is active */}
      {heroMode && (
        <>
          {/* Directly under the search box. Search is the front door for
              people who arrived with a title in mind; this answers "what's
              happening" for everyone who did not, which a search box cannot do
              on its own — it only answers questions you already knew to ask.
              Poster rows grouped by when, not a month grid: the grid is a
              lookup tool and gives a first-time visitor nothing to look at.
              The grid stays at /calendar for the "when is X out" case. */}
          <UpcomingByDate />

          {/* VideBits Insider — film/TV in development */}
          <InsiderCarousel apiBase={API_BASE} />

          {/* Daily intelligence feed (docs/MVP.md P5) — production/casting
              news + trending searches. Releases/streaming/trailers are
              covered above by UpcomingByDate, not repeated here. */}
          <IntelligenceFeed apiBase={API_BASE} />

          {/* Carousels (data-driven; default landing layout) */}
          <DashboardManager apiBase={API_BASE} isAuthenticated={isAuthenticated} token={token} />

          {/* Features section */}
          <div className="bg-white py-16 px-4 border-t border-gray-100">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
                {[
                  {
                    icon: Search,
                    title: 'Discover',
                    desc: 'Ask naturally across film, TV and games to find what to watch, play and experience next.',
                    to: '/',
                    iconColor: 'text-videbits-500',
                    iconBg: 'bg-videbits-50',
                  },
                  {
                    icon: CalendarDays,
                    title: 'Calendar',
                    desc: 'Track what is happening next—from theatrical and streaming releases to season premieres and game launches.',
                    to: '/calendar',
                    iconColor: 'text-blue-500',
                    iconBg: 'bg-blue-50',
                  },
                  {
                    icon: Zap,
                    title: 'Industry intelligence',
                    desc: 'Follow projects, people, companies and emerging signals across entertainment before they become obvious.',
                    to: '/insider',
                    iconColor: 'text-amber-500',
                    iconBg: 'bg-amber-50',
                  },
                ].map(({ icon: Icon, title, desc, to, iconColor, iconBg }) => (
                  <Link key={title} to={to} className="text-center px-4 group">
                    <div className={`w-14 h-14 ${iconBg} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-105 transition-transform`}>
                      <Icon className={`w-7 h-7 ${iconColor}`} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-videbits-700 transition-colors">{title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Search state — results (the search bar itself lives in the always-
          rendered band above, so it stays mounted and keeps focus). */}
      {hasSearched && (
      <main className="px-4 py-6 md:py-10 flex-1">

        {/* AI intelligence layer — runs on every search, shown above the
            catalogue results when the query is an intelligence question. */}
        <IntelligentResults query={aiQuery} apiBase={API_BASE} augment />

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 max-w-4xl mx-auto">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Catalogue provider unavailable — degraded search */}
        {searchDegraded && !loading && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 max-w-4xl mx-auto text-sm text-amber-800">
            Search is temporarily limited — our catalogue provider is unavailable. Showing limited results; please try again shortly.
          </div>
        )}

        {/* Live sport isn't in the catalogue — surface where to watch it. */}
        {sportsResult && !loading && (
          <div className="max-w-4xl mx-auto mb-6 bg-gradient-to-r from-videbits-50 to-videbits-100 border border-videbits-200 rounded-xl p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Watch {sportsResult.competition} live</h3>
            <p className="text-sm text-gray-600 mb-3">Live sport isn't in the catalogue — here's where to stream {sportsResult.competition}:</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {sportsResult.services.map((s) => (
                <a key={`${s.name}-${s.region}`} href={s.url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-videbits-50 border border-videbits-200 hover:border-videbits-400 transition-colors">
                  <span className="text-sm font-medium text-gray-900">{s.name}</span>
                  <span className="text-xs text-gray-500">
                    {s.is_free ? 'Free' : `${s.currency === 'GBP' ? '£' : '$'}${s.monthly_price.toFixed(2)}/mo`} · {s.region}
                  </span>
                </a>
              ))}
            </div>
            <Link to="/sports" className="text-sm font-medium text-videbits-600 hover:underline">Compare sports services →</Link>
          </div>
        )}

        {didYouMean && !loading && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 max-w-4xl mx-auto text-sm text-blue-700">
            Did you mean:{' '}
            <button
              className="text-btn font-semibold underline hover:text-blue-900"
              onClick={() => {
                setSearchQuery(didYouMean);
                debouncedSearch(didYouMean);
              }}
            >
              {didYouMean}
            </button>
            ?
          </div>
        )}

        {/* Loading Indicator */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 max-w-4xl mx-auto">
            <div className="relative">
              {/* Outer rotating ring */}
              <div className="w-24 h-24 border-4 border-videbits-200 border-t-videbits-600 rounded-full animate-spin"></div>
              {/* Inner pulsing circle */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="w-16 h-16 bg-gradient-to-br from-videbits-400 to-videbits-600 rounded-full animate-pulse flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="mt-6 text-center">
              <p className="text-lg font-semibold text-gray-700 animate-pulse">Thinking...</p>
              <p className="text-sm text-gray-500 mt-2">Finding the best matches for you</p>
            </div>
          </div>
        )}

        {/* Search Results */}
        {!loading && searchResults.length > 0 && (
          <div className="space-y-8 max-w-4xl mx-auto">
            {/* LLM Enhancement Badge */}
            {console.log('🔍 Rendering results, llmMetadata.enhanced:', llmMetadata.enhanced)}
            {llmMetadata.enhanced && (
              <div className="flex items-center justify-center space-x-2 mb-4">
                {console.log('🎨 Rendering LLM Badge!')}
                <div className="bg-gradient-to-r from-videbits-400 to-videbits-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center space-x-2 animate-pulse-subtle">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 7H7v6h6V7z"/>
                    <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd"/>
                  </svg>
                  <span className="font-semibold text-sm">
                    AI-Enhanced Search
                    {llmMetadata.provider && ` (${llmMetadata.provider.toUpperCase()})`}
                  </span>
                  {llmMetadata.confidence && (
                    <span className="text-xs opacity-90">
                      {Math.round(llmMetadata.confidence * 100)}% confidence
                    </span>
                  )}
                </div>
                {llmMetadata.reasoning && (
                  <div className="group relative">
                    <button className="text-videbits-600 hover:text-videbits-800 transition-colors">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                      </svg>
                    </button>
                    <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-64 left-1/2 transform -translate-x-1/2 mt-2 shadow-xl">
                      <div className="font-semibold mb-1">AI Understanding:</div>
                      <div className="opacity-90">{llmMetadata.reasoning}</div>
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {(parsedSearch.category || parsedSearch.crew || parsedSearch.tags) && (
              <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
                <Filter className="w-4 h-4" />
                <span>Filtered by:</span>
                {parsedSearch.category && <span className="bg-videbits-100 text-videbits-800 px-2 py-1 rounded text-xs">{parsedSearch.category}</span>}
                {parsedSearch.crew && <span className="bg-videbits-200 text-videbits-900 px-2 py-1 rounded text-xs">Crew: {parsedSearch.crew}</span>}
                {parsedSearch.tags && <span className="bg-videbits-100 text-videbits-800 px-2 py-1 rounded text-xs">Tags: {parsedSearch.tags}</span>}
              </div>
            )}


            {searchResults.map((result, index) => {
              // Render person results differently
              if (result.category === 'Person') {
                return (
                  <Link
                    key={result.id}
                    to={`/person/${result.id}`}
                    onClick={() => {
                      sessionStorage.setItem(`person_${result.id}`, JSON.stringify(result));
                      logInteraction('result_click', {
                        tmdbId: result.id, mediaType: 'person', title: result.title,
                        query: searchQuery, rank: index + 1, resultsCount: searchResults.length,
                      });
                    }}
                    className="block border-b border-gray-100 pb-6 last:border-b-0 hover:bg-videbits-50 -mx-4 px-4 py-4 rounded-lg transition-colors cursor-pointer"
                  >
                    <div className="flex items-start space-x-4">
                      {/* Profile Picture */}
                      <div className="flex-shrink-0">
                        {result.poster_path ? (
                          <img
                            src={result.poster_path}
                            alt={result.title}
                            className="w-16 h-16 rounded-full object-cover border-2 border-videbits-200"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-videbits-100 flex items-center justify-center border-2 border-videbits-200">
                            <User className="w-8 h-8 text-videbits-500" />
                          </div>
                        )}
                      </div>

                      {/* Person Info */}
                      <div className="flex-1">
                        <h4 className="text-lg font-medium text-gray-900 leading-tight mb-2">
                          <SafeHighlight text={result.title} query={searchQuery} />
                        </h4>

                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          <SafeHighlight
                            text={result.content.substring(0, 200) + (result.content.length > 200 ? '...' : '')}
                            query={searchQuery}
                          />
                        </p>

                        <div className="flex items-center text-xs text-gray-500 space-x-4">
                          <div className="flex items-center space-x-1">
                            <User className="w-3 h-3" />
                            <span>{result.category}</span>
                          </div>
                        </div>

                        {/* Tags */}
                        <TagList tags={result.tags} limit={5} onTagClick={handleTagClick} variant="brand" />
                      </div>
                    </div>
                  </Link>
                );
              }

              // Render movie/TV results as before
              return (
                <Link
                  key={result.id}
                  to={searchResultHref(result)}
                  onClick={() => {
                    // Store movie data for the detail view
                    sessionStorage.setItem(`movie_${result.id}`, JSON.stringify(result));
                    logInteraction('result_click', {
                      tmdbId: result.id,
                      mediaType: result.category === 'TV Series' ? 'tv' : 'movie',
                      title: result.title,
                      query: searchQuery,
                      rank: index + 1,
                      resultsCount: searchResults.length,
                    });
                  }}
                  className="block border-b border-gray-100 pb-6 last:border-b-0 hover:bg-gray-50 -mx-4 px-4 py-4 rounded-lg transition-colors cursor-pointer hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-lg font-medium text-gray-900 leading-tight hover:text-videbits-600 transition-colors mb-2">
                        <SafeHighlight text={result.title} query={searchQuery} />
                      </h4>

                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        <SafeHighlight
                          text={result.content.substring(0, 200) + (result.content.length > 200 ? '...' : '')}
                          query={searchQuery}
                        />
                      </p>

                      <div className="flex items-center text-xs text-gray-500 space-x-4">
                        <div className="flex items-center space-x-1">
                          <Tag className="w-3 h-3" />
                          <span>{result.category}</span>
                        </div>
                        <span>•</span>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(result.created_at)}</span>
                        </div>
                        {result.rank && (
                          <>
                            <span>•</span>
                            <div className="flex items-center space-x-1">
                              <TrendingUp className="w-3 h-3" />
                              <span>Relevance: {(result.rank * 100).toFixed(1)}%</span>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Tags */}
                      <TagList tags={result.tags} limit={3} onTagClick={handleTagClick} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {searchQuery && searchResults.length === 0 && !loading && !error && !searchDegraded && (
          <div className="text-center py-12 max-w-4xl mx-auto">
            <div className="text-gray-400 mb-4">
              <Search className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-600">
              Try searching for a film, TV show, game, person, company or genre
            </p>
          </div>
        )}

      </main>
      )}

      {/* Release-digest signup band */}
      <section id="mailing-list" className="bg-videbits-50 border-t border-videbits-100">
        <div className="max-w-2xl mx-auto px-4 py-12 md:py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-videbits-900 mb-2">Never miss a release</h2>
          <p className="text-videbits-700 mb-6">
            Get a weekly email of upcoming film, TV and game releases—including theatrical, streaming and season premieres.
          </p>
          {digestStatus === 'ok' ? (
            // Double opt-in: nothing joins the list until the emailed link is
            // clicked. The API returns the same payload whether a confirmation
            // was sent or the address was already subscribed, so it never
            // reveals who is on the list — this copy is true either way.
            <p className="text-green-700 font-medium">
              Almost there — check your inbox and click the confirmation link to
              start getting release alerts.
            </p>
          ) : (
            <form onSubmit={submitDigest} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                required
                value={digestEmail}
                onChange={(e) => setDigestEmail(e.target.value)}
                placeholder="you@example.com"
                aria-label="Email address"
                className="flex-1 px-4 py-3 rounded-xl border border-videbits-200 focus:outline-none focus:ring-2 focus:ring-videbits-400"
              />
              <button
                type="submit"
                disabled={digestStatus === 'sending'}
                className="px-5 py-3 bg-videbits-600 text-white rounded-xl font-semibold hover:bg-videbits-700 transition-colors disabled:opacity-50"
              >
                {digestStatus === 'sending' ? 'Sending…' : 'Get release alerts →'}
              </button>
            </form>
          )}
          {digestStatus === 'error' && (
            <p className="text-red-600 text-sm mt-3">
              {digestError || 'Please enter a valid email and try again.'}
            </p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-gray-50 mt-12 md:mt-16">
        <div className="max-w-6xl mx-auto px-4 py-6 md:py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-wrap justify-center md:justify-start items-center gap-4 md:gap-6 text-sm text-gray-600">
              <Link to="/about" className="hover:text-videbits-600 transition-colors">About</Link>
              <Link to="/privacy" className="hover:text-videbits-600 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-videbits-600 transition-colors">Terms</Link>
              <Link to="/help" className="hover:text-videbits-600 transition-colors">Help</Link>
            </div>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <a
                href="https://www.facebook.com/videbits"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="VideBits on Facebook"
                className="hover:text-videbits-600 transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <span>© {new Date().getFullYear()} VideBits Ltd</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        initialMode={loginModalMode}
      />
    </div>
  );
};

export default Home;
